/* ============================================================
   WhatsApp Reviews slider
   - Native CSS scroll-snap handles the actual scrolling/swiping.
   - JS adds: arrow buttons, pagination dots, autoplay (pause on
     hover/focus), RTL awareness, and theme-editor re-init.
   ============================================================ */
(function () {
  'use strict';

  var instances = [];

  function isRTL() {
    return document.documentElement.dir === 'rtl';
  }

  function WAReviews(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-wa-viewport]');
    this.track = root.querySelector('[data-wa-track]');
    this.slides = root.querySelectorAll('[data-wa-slide]');
    this.prevBtn = root.querySelector('[data-wa-prev]');
    this.nextBtn = root.querySelector('[data-wa-next]');
    this.dotsWrap = root.querySelector('[data-wa-dots]');
    this.autoplayMs = parseInt(root.getAttribute('data-autoplay'), 10) || 0;

    this.dots = [];
    this.timer = null;
    this.activeIndex = 0;

    if (!this.viewport || !this.track || !this.slides.length) {
      return;
    }

    this.bind();
    this.buildDots();
    this.update();
  }

  WAReviews.prototype.slideStep = function () {
    // Distance between two slide starts = slide width + gap.
    if (this.slides.length < 2) {
      return this.slides[0].offsetWidth;
    }
    return Math.abs(this.slides[1].offsetLeft - this.slides[0].offsetLeft);
  };

  WAReviews.prototype.visibleCount = function () {
    var step = this.slideStep();
    if (!step) return 1;
    return Math.max(1, Math.round(this.viewport.clientWidth / step));
  };

  WAReviews.prototype.maxIndex = function () {
    return Math.max(0, this.slides.length - this.visibleCount());
  };

  WAReviews.prototype.bind = function () {
    var self = this;

    // Arrows. In RTL the visual "previous" still means earlier-in-DOM,
    // but scrollLeft runs negative, so we flip the sign accordingly.
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        self.go(self.activeIndex - 1);
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        self.go(self.activeIndex + 1);
      });
    }

    // Keep active index / dots in sync while the user scrolls or swipes.
    var ticking = false;
    this.viewport.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        self.syncFromScroll();
        ticking = false;
      });
    }, { passive: true });

    // Autoplay pause on interaction.
    if (this.autoplayMs > 0) {
      ['mouseenter', 'focusin', 'touchstart'].forEach(function (evt) {
        self.root.addEventListener(evt, function () { self.stop(); }, { passive: true });
      });
      ['mouseleave', 'focusout'].forEach(function (evt) {
        self.root.addEventListener(evt, function () { self.start(); });
      });
      this.start();
    }

    window.addEventListener('resize', function () {
      self.buildDots();
      self.update();
    });
  };

  WAReviews.prototype.scrollToIndex = function (index) {
    var step = this.slideStep();
    var offset = step * index;
    this.viewport.scrollTo({
      left: isRTL() ? -offset : offset,
      behavior: 'smooth'
    });
  };

  WAReviews.prototype.go = function (index) {
    var max = this.maxIndex();
    if (index < 0) index = 0;
    if (index > max) index = max;
    this.activeIndex = index;
    this.scrollToIndex(index);
    this.update();
  };

  WAReviews.prototype.syncFromScroll = function () {
    var step = this.slideStep();
    if (!step) return;
    var pos = Math.abs(this.viewport.scrollLeft);
    this.activeIndex = Math.min(this.maxIndex(), Math.round(pos / step));
    this.update();
  };

  WAReviews.prototype.buildDots = function () {
    if (!this.dotsWrap) return;
    var self = this;
    var pages = this.maxIndex() + 1;

    // Rebuild only when the count changes (e.g. after resize).
    if (this.dots.length === pages) return;

    this.dotsWrap.innerHTML = '';
    this.dots = [];

    for (var i = 0; i < pages; i++) {
      (function (idx) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'whatsapp-reviews__dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', self.goToLabel(idx + 1));
        dot.addEventListener('click', function () {
          self.stop();
          self.go(idx);
        });
        self.dotsWrap.appendChild(dot);
        self.dots.push(dot);
      })(i);
    }
  };

  WAReviews.prototype.goToLabel = function (n) {
    // Template seeds the localized string on the dots wrapper if available.
    var tpl = this.dotsWrap ? this.dotsWrap.getAttribute('data-go-to-label') : null;
    if (tpl) return tpl.replace('__N__', n);
    return 'Go to review ' + n;
  };

  WAReviews.prototype.update = function () {
    var max = this.maxIndex();

    if (this.prevBtn) this.prevBtn.disabled = this.activeIndex <= 0;
    if (this.nextBtn) this.nextBtn.disabled = this.activeIndex >= max;

    for (var i = 0; i < this.dots.length; i++) {
      this.dots[i].setAttribute('aria-selected', i === this.activeIndex ? 'true' : 'false');
    }
  };

  WAReviews.prototype.start = function () {
    if (this.autoplayMs <= 0) return;
    var self = this;
    this.stop();
    this.timer = window.setInterval(function () {
      var next = self.activeIndex + 1;
      if (next > self.maxIndex()) next = 0;
      self.go(next);
    }, this.autoplayMs);
  };

  WAReviews.prototype.stop = function () {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  };

  WAReviews.prototype.destroy = function () {
    this.stop();
  };

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-wa-reviews]');
    roots.forEach(function (root) {
      if (root.__waReviews) return;
      root.__waReviews = new WAReviews(root);
      instances.push(root.__waReviews);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Theme editor: re-init when this section is (re)loaded.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-wa-reviews]') ||
      (e.target.matches && e.target.matches('[data-wa-reviews]') ? e.target : null);
    if (root && root.__waReviews) {
      root.__waReviews.destroy();
      root.__waReviews = null;
    }
  });
})();
